import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from '@inertiajs/react';
import { BookOpen, MessageCircle, Activity as ActivityIcon, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DashboardActivity {
  type: 'journal' | 'chat' | 'login';
  description: string;
  timestamp: string;
  link?: string;
}

interface RecentActivityProps {
  activities: DashboardActivity[];
}

export function RecentActivity({ activities }: RecentActivityProps) {
  const getActivityIcon = (type: DashboardActivity['type']) => {
    switch (type) {
      case 'journal':
        return BookOpen;
      case 'chat':
        return MessageCircle;
      default:
        return ActivityIcon;
    }
  };

  const getActivityColor = (type: DashboardActivity['type']) => {
    switch (type) {
      case 'journal':
        return 'text-green-600';
      case 'chat':
        return 'text-purple-600';
      default:
        return 'text-blue-600';
    }
  };

  const getActivityBgColor = (type: DashboardActivity['type']) => {
    switch (type) {
      case 'journal':
        return 'bg-green-100 dark:bg-green-900/20';
      case 'chat':
        return 'bg-purple-100 dark:bg-purple-900/20';
      default:
        return 'bg-blue-100 dark:bg-blue-900/20';
    }
  };

  const formatRelativeTime = (timestamp: string) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - activityTime.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Ora';
    if (diffInMinutes < 60) return `${diffInMinutes}m fa`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h fa`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}g fa`;

    return activityTime.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short'
    });
  };

  if (activities.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
              <ActivityIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </div>
            Attività recente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <ActivityIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Nessuna attività recente</p>
            <p className="text-gray-400 text-xs mt-1">Le tue azioni appariranno qui</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
            <ActivityIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </div>
          Attività recente
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {activities.map((activity, index) => {
            const IconComponent = getActivityIcon(activity.type);
            const iconColor = getActivityColor(activity.type);
            const bgColor = getActivityBgColor(activity.type);

            const activityContent = (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                <div className={cn('p-2 rounded-lg flex-shrink-0', bgColor)}>
                  <IconComponent className={cn('h-4 w-4', iconColor)} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {activity.description}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <Clock className="h-3 w-3 text-gray-400" />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatRelativeTime(activity.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            );

            return (
              <div key={index}>
                {activity.link ? (
                  <Link href={activity.link} className="block">
                    {activityContent}
                  </Link>
                ) : (
                  activityContent
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
